import { Grid, Typography } from "@sidelinesports/fan-design-system";
import type { ApiGameHistorical } from "../../../api/game-history.ts";
import { FormattedDate } from "../../../components/FormattedDate.tsx";
import { UploadedImage } from "../../../components/UploadedImage.tsx";
import { useNumberFormatter } from "../../../hooks/use-number-formatter.ts";
import { useOrdinalFormatter } from "../../../hooks/use-ordinal-formatter.ts";
import { getGameShortTitle } from "../../../utils/game-title.ts";
import type { useGameHistory } from "../hooks/use-game-history.ts";
import styles from "./GameHistoryTable.module.css";

export const GameHistoryTable = ({
  history,
}: {
  history: ReturnType<typeof useGameHistory>["data"];
}) => {
  const formatRank = useOrdinalFormatter();
  const formatPoints = useNumberFormatter();

  if (!history || history.length === 0) {
    return null;
  }

  return (
    <table className={styles.table}>
      <thead className={styles.headers}>
        <tr>
          <th className={styles["game-header-cell"]}>Game</th>
          <th>Points</th>
          <th>Rank</th>
        </tr>
      </thead>
      <tbody className={styles.body}>
        {history.map((game) => (
          <tr key={game.id} className={styles.row}>
            <td>
              <GameImage game={game} />
            </td>
            <td>
              <Grid gap={2}>
                <Typography size="md">{getGameShortTitle(game)}</Typography>
                <Typography size="sm" color="muted">
                  <FormattedDate
                    value={game.timestamp}
                    month="numeric"
                    day="numeric"
                    year="numeric"
                  />
                </Typography>
              </Grid>
            </td>
            <td className={styles["points-cell"]}>
              <Typography size="md">
                {formatPoints(game.leaderboard?.totalPoints ?? 0)}
              </Typography>
            </td>
            <td className={styles["rank-cell"]}>
              <Typography size="md">
                {game.leaderboard?.rank
                  ? formatRank(game.leaderboard.rank)
                  : "—"}
              </Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const GameImage = ({ game }: { game: ApiGameHistorical }) => {
  const { image, alt } = getGameImage(game);

  if (!image) {
    return null;
  }

  return (
    <UploadedImage
      alt={alt}
      image={image}
      w="2rem"
      h="2rem"
      style={{ flexShrink: 0 }}
      tracking={{ enabled: true, location: "GameHistoryDialog::GameImage" }}
    />
  );
};

function getGameImage(game: ApiGameHistorical) {
  if (game.typeEvent) {
    return { image: game.typeEvent.image, alt: game.typeEvent.name };
  }

  if (game.typeMatchup) {
    return {
      image: game.typeMatchup.opponent.logo,
      alt: `${game.typeMatchup.opponent.name} logo`,
    };
  }

  return {};
}
